import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import {Button, Icon, Modal} from '@drivesally/sally-ui';
import {grey} from '@drivesally/sally-ui/lib/libs/colors';

import listRentalsApi from '@drivesally/sally-fn/lib/rental/api/list.api.rental';
import {isIn} from '@drivesally/sally-fn/lib/libs/apiQueryFilterExpressions';
import activeState from '@drivesally/sally-fn/lib/rental/state/active.state.rental';
import reservedState from '@drivesally/sally-fn/lib/rental/state/reserved.state.rental';
import returnedState from '@drivesally/sally-fn/lib/rental/state/returned.state.rental';

import getBranch from '../../../branch/lib/getBranch.lib.branch';
import * as driverDocuments from '@drivesally/sally-fn/lib/driver/documents/folders';
import * as vehicleDocuments from '@drivesally/sally-fn/lib/vehicle/documents/folders';
import getDocumentsList from '@drivesally/sally-fn/lib/document/libs/getList.lib.document';

import CrmDocumentBrowser from '../CrmDocumentBrowser/CrmDocumentBrowser';

const Layout = styled.div`
  display: flex;
  max-height: calc(100vh - 160px);
  min-height: 480px;
  overflow: hidden;
`;

const Column = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
`;

const ColumnHeader = styled.div`
  border-bottom: 1px solid ${grey};
  color: #333;
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 600;
  padding: 12px 16px;
`;

const ColumnDivider = styled.div`
  background: ${grey};
  flex-shrink: 0;
  width: 1px;
`;

const ColumnScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

const EmptyRow = styled.div`
  align-items: center;
  color: #888;
  display: flex;
  font-size: 13px;
  justify-content: center;
  padding: 40px 16px;
`;

const VehicleHeader = styled.button`
  align-items: center;
  background: none;
  border: none;
  border-bottom: 1px solid ${grey};
  cursor: pointer;
  display: flex;
  font-size: 12px;
  gap: 10px;
  padding: 10px;
  text-align: left;
  width: 100%;

  &:hover {
    background: ${grey};
  }
`;

const VehicleIcon = styled.span`
  color: #04A9F4;
  font-size: 24px;
  line-height: 1;
  width: 24px;
`;

const VehicleLabel = styled.span`
  color: #263238;
  flex: 1;
`;

const VehicleState = styled.span`
  color: #8EA1AC;
  font-size: 11px;
  font-weight: 400;
  text-transform: capitalize;
`;

const PreviewColumn = styled.div`
  display: flex;
  flex: 1.5;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
`;

const PreviewArea = styled.div`
  align-items: center;
  background: #f5f5f5;
  display: flex;
  flex: 1;
  height: 0;
  justify-content: center;
  overflow: hidden;
  padding: 16px;
`;

const PreviewImage = styled.img`
  max-height: 100%;
  max-width: 100%;
  object-fit: contain;
`;

const PreviewFrame = styled.iframe`
  border: none;
  height: 100%;
  width: 100%;
`;

const PreviewEmpty = styled.div`
  color: #8EA1AC;
  font-size: 13px;
  text-align: center;
`;

const PreviewFooter = styled.div`
  border-top: 1px solid ${grey};
  display: flex;
  flex-shrink: 0;
  justify-content: flex-end;
  padding: 12px 16px;
`;

const VehicleDocs = styled.div`
  border-bottom: 1px solid ${grey};
`;

const CrmDriverDocPickerModal = ({driver, onClose, onSend, visible}) => {
  const [rentals, setRentals] = useState([]);
  const [rentalsLoading, setRentalsLoading] = useState(false);
  const [expandedVehicleId, setExpandedVehicleId] = useState(null);
  const [mountedVehicleIds, setMountedVehicleIds] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState(null);

  const driverId = driver?.id;

  useEffect(() => {
    if (!visible || !driverId) return;
    setRentalsLoading(true);
    setRentals([]);
    setExpandedVehicleId(null);
    setMountedVehicleIds([]);
    setSelectedDoc(null);

    listRentalsApi({
      rentaldriver__driver: driverId,
      ordering: '-start_date',
      [isIn('state')]: [activeState.key, reservedState.key, returnedState.key].join(','),
    })
      .then((response) => {
        setRentals(response?.results || []);
        setRentalsLoading(false);
      })
      .catch(() => {
        setRentalsLoading(false);
      });
  }, [visible, driverId]);

  const toggleVehicle = (vehicleId) => {
    setMountedVehicleIds((prev) => prev.includes(vehicleId) ? prev : [...prev, vehicleId]);
    setExpandedVehicleId((prev) => (prev === vehicleId ? null : vehicleId));
  };

  const driverPredefinedDocs = driver
    ? getDocumentsList({branch: getBranch(driver.sally_branch), documents: driverDocuments, type: 'driver'})
    : null;

  const fmt = (selectedDoc?.file_format || selectedDoc?.file_extension || '').toLowerCase();
  const isImage = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(fmt);
  const isPdf = fmt === 'pdf';

  return (
    <Modal title="Driver Documents" size="full" visible={visible} onClose={onClose} disableOutsideClickClose>
      {(Content) => (
        <Content padding="none" noBorder>
          <Layout>
            <Column>
              <ColumnHeader>Driver Documents</ColumnHeader>
              <ColumnScroll>
                {driver && (
                  <CrmDocumentBrowser
                    entityId={driver.id}
                    entityType="driver"
                    predefinedDocs={driverPredefinedDocs}
                    onSelect={setSelectedDoc}
                    selectedDocId={selectedDoc?.id}
                    visible={visible}
                  />
                )}
              </ColumnScroll>
            </Column>
            <ColumnDivider />
            <Column>
              <ColumnHeader>Vehicle Documents</ColumnHeader>
              <ColumnScroll>
                {rentalsLoading ? (
                  <EmptyRow>
                    <Icon icon="loading2" spin />
                  </EmptyRow>
                ) : !rentals.length ? (
                  <EmptyRow>No active rentals found</EmptyRow>
                ) : (
                  rentals.map((rental) => {
                    const vehicle = rental.vehicle;
                    const isExpanded = expandedVehicleId === vehicle.id;
                    const vehiclePredefinedDocs = getDocumentsList({branch: getBranch(vehicle.sally_branch), documents: vehicleDocuments, type: 'vehicle'});
                    return (
                      <div key={rental.id}>
                        <VehicleHeader onClick={() => toggleVehicle(vehicle.id)}>
                          <VehicleIcon>
                            <Icon icon="car" />
                          </VehicleIcon>
                          <VehicleLabel>
                            {vehicle.svid || `Vehicle ${vehicle.id}`}
                          </VehicleLabel>
                          <VehicleState>{rental.state}</VehicleState>
                        </VehicleHeader>
                        {mountedVehicleIds.includes(vehicle.id) && (
                          <VehicleDocs style={{display: isExpanded ? 'block' : 'none'}}>
                            <CrmDocumentBrowser
                              entityId={vehicle.id}
                              entityType="vehicle"
                              predefinedDocs={vehiclePredefinedDocs}
                              onSelect={setSelectedDoc}
                              selectedDocId={selectedDoc?.id}
                              visible={isExpanded}
                            />
                          </VehicleDocs>
                        )}
                      </div>
                    );
                  })
                )}
              </ColumnScroll>
            </Column>
            <ColumnDivider />
            <PreviewColumn>
              <ColumnHeader>Preview</ColumnHeader>
              <PreviewArea>
                {selectedDoc ? (
                  isImage ? (
                    <PreviewImage src={selectedDoc.document_file} alt={selectedDoc.name} />
                  ) : isPdf ? (
                    <PreviewFrame src={selectedDoc.document_file} title={selectedDoc.name} />
                  ) : (
                    <PreviewEmpty>No Preview Available</PreviewEmpty>
                  )
                ) : (
                  <PreviewEmpty>Select a document to preview</PreviewEmpty>
                )}
              </PreviewArea>
              <PreviewFooter>
                <Button
                  theme="red"
                  size="small"
                  disabled={!selectedDoc}
                  onClick={() => { onSend(selectedDoc); onClose(); }}
                >
                  Send
                </Button>
              </PreviewFooter>
            </PreviewColumn>
          </Layout>
        </Content>
      )}
    </Modal>
  );
};

CrmDriverDocPickerModal.propTypes = {
  driver: PropTypes.object,
  onClose: PropTypes.func,
  onSend: PropTypes.func,
  visible: PropTypes.bool,
};

export default CrmDriverDocPickerModal;
